// subagentTools.js — F13-E13-T02/T03 (+E5-T05/E6-T01): subagentes TIPADOS como
// tools aninhadas, padrão Claude Code. O loop principal DELEGA e recebe só a
// CONCLUSÃO — o contexto principal não é inundado com dumps de arquivo.
//
//   explore     — fan-out de busca READ-ONLY (grep/read/list/semantic) num loop
//                 filho com contexto limpo; devolve conclusões (paths, símbolos,
//                 fluxo), nunca arquivos inteiros.
//   code_review — revisão FRESH-EYES (E15-T08): o filho vê SÓ o diff + a
//                 pergunta (sem o racional de quem implementou) e aponta bugs
//                 de correção/risco com arquivo:linha.
//
// SEM recursão: o loop filho roda com extraTools=[] (não enxerga explore/
// code_review) + readOnly (MUTATING_TOOLS negadas no gate) + iterações capadas.

import { runTool } from './agentTools.js';
import { subworkPath } from './subworkPath.js';

const NESTED_MAX_ITERATIONS = 10;
const MAX_DIFF_CHARS = 24000;

export const EXPLORE_DEF = {
  type: 'function',
  function: {
    name: 'explore',
    description:
      'Delega uma PERGUNTA de exploração do código a um subagente READ-ONLY (grep/read/list/semantic com contexto limpo) e devolve só as CONCLUSÕES (paths exatos, símbolos, como o fluxo funciona) — seu contexto não recebe os dumps. '
      + 'Use quando responder exige varrer VÁRIOS arquivos/convenções ("onde X é tratado?", "como Y flui até Z?"). NÃO use pra 1 arquivo que você já conhece (read_file direto é mais barato). Ex: {"question":"onde o loop decide re-steer e quais nudges existem?"}.',
    parameters: {
      type: 'object',
      properties: { question: { type: 'string', description: 'A pergunta de exploração, específica e respondível.' } },
      required: ['question'],
    },
  },
};

export const CODE_REVIEW_DEF = {
  type: 'function',
  function: {
    name: 'code_review',
    description:
      'Revisão FRESH-EYES do diff atual (git diff) por um subagente de contexto LIMPO — ele vê só o diff, sem o seu racional, e aponta bugs de correção, riscos e simplificações com arquivo:linha. '
      + 'Use ANTES de finalizar mudança multi-arquivo ou arriscada; corrija o que procede antes do done. Ex: {} ou {"focus":"segurança"}.',
    parameters: {
      type: 'object',
      properties: { focus: { type: 'string', description: 'Opcional: dimensão de foco (segurança, perf, correção).' } },
      required: [],
    },
  },
};

// Nota-10 subagents (07-07) — WRITER delegável em WORKTREE ISOLADO. O filho
// escreve à vontade num descartável (git worktree --detach); NADA vaza: o pai
// recebe o DIFF PROPOSTO e aplica pelos próprios tools gated (propõe-nunca-
// dispõe, doutrina #413). Worktree por filho = zero clobber (lição W1).
const WRITER_MAX_ITERATIONS = 14;
const IMPLEMENT_DEF = {
  type: 'function',
  function: {
    name: 'delegate_implement',
    description:
      'Delegue uma sub-tarefa de IMPLEMENTAÇÃO bem-escopada a um subagente ESCRITOR que trabalha num worktree git ISOLADO e descartável. '
      + 'Ele NÃO toca seu worktree: você recebe o DIFF proposto e aplica o que proceder com apply_edit/write_file. '
      + 'Use para partes paralelizáveis/mecânicas de uma mudança maior. Ex: {"task":"extrair helper X de Y pra lib/z.js com teste"}.',
    parameters: {
      type: 'object',
      properties: {
        task: { type: 'string', description: 'Sub-tarefa de implementação, específica e autocontida.' },
        files: { type: 'string', description: 'Opcional: paths-alvo (orienta o escritor).' },
      },
      required: ['task'],
    },
  },
};

function buildWriterPrompt(task, files) {
  return [
    'Você é o subagente ESCRITOR (worktree isolado e descartável — seu diff será revisado e aplicado pelo agente principal).',
    'EDITE arquivos com write_file/apply_edit e, se barato, verifique com run_command (teste do arquivo tocado).',
    'NÃO rode git commit/branch/push/gh — deixe tudo NO WORKING TREE. Não crie arquivos fora do necessário.',
    files ? `Paths-alvo sugeridos: ${files}` : '',
    `Sub-tarefa: ${task}`,
  ].filter(Boolean).join('\n');
}

// Executa o WRITER num worktree git isolado e descartável e devolve o DIFF
// proposto (nada é aplicado — o pai aplica via tools gated). Extraído do
// resolveSubagentTools pra manter a fábrica sob o teto de LOC. `deps` = o
// contexto da sessão (chatFn/root/model/runLoop/sessionId/signal).
async function runWriterSubagent(args, deps) {
  const { chatFn, root, model, runLoop, sessionId, signal } = deps;
  const task = String(args?.task || '').trim();
  if (!task) return 'Erro: delegate_implement exige {"task":"<sub-tarefa não vazia>"}.';
  const wt = subworkPath(task, sessionId);
  const add = String(await runTool('run_command', { command: `git worktree add --detach "${wt}" HEAD && echo WT_OK` }, { root, sessionId }) || '');
  if (!add.includes('WT_OK')) return `Erro: não consegui criar o worktree isolado (${add.slice(0, 160)}).`;
  try {
    const r = await runLoop({
      chatFn, root: wt, model, sessionId, signal,
      messages: [{ role: 'user', content: buildWriterPrompt(task, String(args?.files || '').trim()) }],
      taskText: `implement: ${task.slice(0, 60)}`,
      maxIterations: WRITER_MAX_ITERATIONS,
      permissionsMode: 'ignore', // descartável: nada escapa; o PAI aplica via tools gated
      extraTools: [], // anti-recursão
    });
    const diff = String(await runTool('run_command', { command: 'git add -N . && git diff' }, { root: wt, sessionId }) || '').slice(0, MAX_DIFF_CHARS);
    if (!/^diff --git/m.test(diff)) return `(subagente escritor não produziu mudanças — stopReason: ${r?.stopReason || '?'}. Refine a sub-tarefa ou faça você mesmo.)`;
    return `DIFF PROPOSTO pelo escritor (worktree isolado; NADA foi aplicado — revise e aplique o que proceder com apply_edit/write_file):\n${diff}`;
  } catch (e) {
    return `Erro: subagente escritor falhou (${e?.message || 'desconhecido'}).`;
  } finally {
    await runTool('run_command', { command: `git worktree remove --force "${wt}"` }, { root, sessionId });
  }
}

// Cross-model review (doutrina "reviewer de OUTRA família" — handover Fable-5/
// Codex: revisor de família diferente acha bugs que o autor-modelo não vê).
// Preferências na ordem; a 1ª de família ≠ do implementador vence. null quando
// só há a própria família (o caller cai no review mesmo-modelo de sempre).
const CROSS_REVIEW_PREFS = ['kimi/kimi-k2.7-code', 'qwen/qwen3-coder-plus', 'deepseek/deepseek-flash'];
export function resolveCrossReviewModel(mainModel, availableModels = null) {
  const fam = String(mainModel || '').split('/')[0];
  // Nota-10 subagents (07-07): a cadeia era ESTÁTICA — se a conta não resolvia a
  // preferida, queimava uma chamada pra falhar antes do retry. Com a lista de
  // modelos RESOLVÍVEIS da conta (buildAvailableModels, ids normalizados #540):
  // preferidas disponíveis primeiro; senão QUALQUER família ≠ (exceto local
  // ollama — reviewer fraco); sem lista = comportamento antigo (fallback só).
  const avail = Array.isArray(availableModels)
    ? availableModels.map((m) => (typeof m === 'string' ? m : m?.id)).filter(Boolean)
    : null;
  const pool = avail ? CROSS_REVIEW_PREFS.filter((m) => avail.includes(m)) : CROSS_REVIEW_PREFS;
  const differentFam = (m) => String(m).split('/')[0] !== fam;
  return pool.find(differentFam)
    || (avail ? avail.find((m) => differentFam(m) && !String(m).startsWith('ollama/')) : null)
    || null;
}

/**
 * Fábrica dos subagentes tipados. Exige worktree (root absoluto) + o runLoop e
 * o chatFn da sessão (mesmo modelo/rota do turno principal). Fora disso ⇒ [].
 * `reviewModel`: modelo de OUTRA família pro code_review (cross-model review);
 * ausente/falhou ⇒ review no modelo do turno (comportamento anterior).
 * @param {{ chatFn?: Function, root?: string, model?: string, runLoop?: Function, sessionId?: string, signal?: AbortSignal, reviewModel?: string|null }} [cfg]
 */
export function resolveSubagentTools({ chatFn, root, model, runLoop, sessionId = null, signal = null, reviewModel = null } = {}) {
  if (typeof chatFn !== 'function' || typeof runLoop !== 'function') return [];
  if (!root || !String(root).startsWith('/')) return [];

  // Dogfood 2026-07-10 (latência) — o subagente read-only re-envia o toolset
  // INTEIRO a cada chamada (~18-19k tokens de input medidos no D1, 20-26s/call no
  // GLM 5.2). Um EXPLORE só precisa LER: read_file/list_files/grep_search/
  // semantic_search. As defs abaixo têm descrições LONGAS e são inúteis pra
  // explorar código — fora do prompt via excludeTools (fix A do #616).
  const NESTED_EXCLUDED_TOOLS = [
    'update_todo', 'present_plan', 'write_feature_list', 'update_feature_status',
    'search_brain', 'preview_console', 'preview_inspect', 'preview_screenshot',
  ];

  const nested = async (content, taskText, modelOverride = null) => {
    try {
      const r = await runLoop({
        chatFn, root, model: modelOverride || model, sessionId, signal,
        messages: [{ role: 'user', content }],
        taskText,
        maxIterations: NESTED_MAX_ITERATIONS,
        permissionsMode: 'deny',
        readOnly: true,
        excludeTools: NESTED_EXCLUDED_TOOLS, // corta defs pesadas do prompt por chamada
        extraTools: [], // anti-recursão: o filho não enxerga explore/code_review
      });
      const text = r?.finalText ? String(r.finalText).trim() : '';
      return text || `(subagente terminou sem conclusão: ${r?.stopReason || 'sem texto'})`;
    } catch (e) {
      return `Erro: subagente falhou (${e?.message || 'desconhecido'}).`;
    }
  };

  return [
    {
      def: EXPLORE_DEF,
      execute: async (args) => {
        const q = String(args?.question || '').trim();
        if (!q) return 'Erro: explore exige {"question":"<pergunta não vazia>"}.';
        return nested(
          [
            'Você é o subagente EXPLORE (read-only, contexto limpo). Responda a pergunta varrendo o worktree com grep_search/read_file/list_files/semantic_search.',
            'Responda só CONCLUSÕES: paths exatos (arquivo:linha), símbolos e o fluxo — NUNCA cole arquivos inteiros. Se não achar, diga o que procurou e onde.',
            `Pergunta: ${q}`,
          ].join('\n'),
          `explore: ${q.slice(0, 60)}`,
        );
      },
    },
    {
      def: CODE_REVIEW_DEF,
      execute: async (args) => {
        const focus = String(args?.focus || '').trim();
        let diff = String(await runTool('run_command', { command: 'git diff && git diff --cached' }, { root, sessionId }) || '').slice(0, MAX_DIFF_CHARS);
        // Achado do A/B 2026-07-06: o agente costuma COMMITAR antes do review →
        // working tree limpo → o reviewer via "diff vazio" e devolvia sem-achados.
        // Fallback: revisa o patch do ÚLTIMO commit (a mudança recém-fechada).
        if (!diff.trim() || /^exitCode:\s*[1-9]/.test(diff.trim())) {
          diff = String(await runTool('run_command', { command: 'git show HEAD --format= --patch' }, { root, sessionId }) || '').slice(0, MAX_DIFF_CHARS);
        }
        if (!diff.trim() || /^exitCode:\s*[1-9]/.test(diff.trim())) {
          return 'Não há diff pra revisar (working tree limpo, sem commits e/ou sem git neste worktree).';
        }
        const content = [
          'Você é o subagente CODE-REVIEWER (fresh-eyes: você NÃO viu o racional de quem escreveu — julgue só o código). Read-only; pode ler arquivos pra confirmar contexto.',
          // "Attack each claim" (handover Fable-5): o review ataca AFIRMAÇÕES implícitas do
          // código, não impressões — cada achado com entrada/interleaving/estado concreto.
          `Revise o diff abaixo${focus ? ` com foco em ${focus}` : ''} de forma ADVERSARIAL: enumere as AFIRMAÇÕES implícitas do código ("mescla intervalos que se tocam", "nunca excede o limite de concorrência", "não muta o input") e ATAQUE cada uma — procure a ENTRADA, o INTERLEAVING (ordem de execução/await) ou o ESTADO que a quebra. Cheque especialmente: bordas (vazio/1 elemento/limites), inputs inválidos, mutação acidental, off-by-one, ordem de resultados vs ordem de término, e caminhos de erro.`,
          // Fable-5 round-2 (07-07): phantom-lock + trust-mismatch — a classe de bug onde a
          // PROMESSA é mais forte que o código (a auditoria de hoje viveu isso: "byte-idêntico"
          // que comparava com artefato velho).
          'CLÁUSULA PHANTOM-LOCK: aponte qualquer lugar onde um comentário, nome ou doc PROMETE um mecanismo que o código não implementa (ex.: "verificado contra hash" sobre um spot-check de tamanho; "thread-safe" sem lock; "idempotente" com efeito colateral). Todo invariante assumido precisa de arquivo:linha que o implemente — promessa sem implementação é achado, mesmo sem bug reproduzível.',
          'Formato: uma linha por achado, "arquivo:linha — afirmação atacada — entrada/cenário concreto que quebra — consequência". Sem achados reais, diga "sem achados" (não invente).',
          '```diff',
          diff,
          '```',
        ].join('\n');
        // Cross-model: revisor de OUTRA família quando fornecido; se o modelo do
        // review falhar (sem rota/credencial), re-tenta no modelo do turno —
        // review mesmo-modelo > review nenhum.
        if (reviewModel && reviewModel !== model) {
          const out = await nested(content, `code_review[${reviewModel}]: diff atual`, reviewModel);
          if (!/^\(subagente terminou sem conclusão|^Erro: subagente falhou/.test(out)) return out;
        }
        return nested(content, 'code_review: diff atual');
      },
    },
    {
      def: IMPLEMENT_DEF,
      // P1 fix (harness-audit 07-13): delegate_implement DISPARA um subagente ESCRITOR
      // (permissionsMode:'ignore'). Sem `mutating:true` ele sobrevivia ao filtro readOnly do
      // plan mode (agentLoop `!(readOnly && t.mutating)`) e ao decideApprovalGate. Marcá-lo
      // como mutante o remove das defs em plan/read-only E o submete à aprovação em modo 'ask'.
      mutating: true,
      execute: (args) => runWriterSubagent(args, { chatFn, root, model, runLoop, sessionId, signal }),
    },
  ];
}
